import apiService from '../api'

export type AgentType = 'SHIPMENT_AGENT' | 'CLEARING_AGENT' | 'FINANCE_AGENT' | 'INVENTORY_AGENT'
export type AgentActionStatus = 'PENDING' | 'APPROVED' | 'EXECUTED' | 'REJECTED'
export type AgentActionType = 'NOTIFY' | 'UPDATE_STATUS' | 'ASSIGN_TASK'

export interface AgentRunApi {
  id: string
  agentType: AgentType
  entityType: string
  entityId: string
  status: string
  aiOutput?: {
    summary?: string
    riskLevel?: 'low' | 'medium' | 'high' | 'critical'
    reasoning?: string
    recommendedActions?: unknown[]
  }
  triggerReason?: string | null
  triggeredBy?: string | null
  createdAt: string
  updatedAt: string
}

export interface AgentActionApi {
  id: string
  agentRunId: string
  actionType: AgentActionType
  target?: string | null
  payload?: Record<string, unknown> | null
  status: AgentActionStatus
  executedAt?: string | null
  executedByUserId?: string | null
  createdAt: string
  updatedAt: string
  agentRun?: AgentRunApi
}

export interface RunAgentResponse {
  runId: string
  actionsCount: number
}

export async function runAgent(
  agentType: AgentType,
  entityId: string,
  triggerReason?: string
): Promise<RunAgentResponse> {
  const path = `/agents/run/${agentType.replace(/_/g, '-').toLowerCase()}/${encodeURIComponent(entityId)}`
  const res = await apiService.post<{ success?: boolean; data?: RunAgentResponse }>(
    path,
    { triggerReason },
    { timeout: 25000 }
  )
  const data = ((res as { data?: RunAgentResponse })?.data ?? res) as RunAgentResponse
  return { runId: data.runId, actionsCount: data.actionsCount ?? 0 }
}

export async function getPendingActions(): Promise<AgentActionApi[]> {
  const res = await apiService.get<{ success?: boolean; data?: { actions: AgentActionApi[] } }>(
    '/agents/actions?status=pending'
  )
  const data = ((res as { data?: { actions: AgentActionApi[] } })?.data ?? res) as { actions: AgentActionApi[] }
  return Array.isArray(data?.actions) ? data.actions : []
}

export async function getActionsForEntity(
  entityType: string,
  entityId: string
): Promise<AgentActionApi[]> {
  const res = await apiService.get<{ success?: boolean; data?: { actions: AgentActionApi[] } }>(
    `/agents/actions/entity?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`
  )
  const data = ((res as { data?: { actions: AgentActionApi[] } })?.data ?? res) as { actions: AgentActionApi[] }
  return Array.isArray(data?.actions) ? data.actions : []
}

export async function approveAction(actionId: string): Promise<void> {
  await apiService.post(`/agents/actions/${encodeURIComponent(actionId)}/approve`, {})
}

export async function rejectAction(actionId: string, reason?: string): Promise<void> {
  await apiService.post(`/agents/actions/${encodeURIComponent(actionId)}/reject`, { reason })
}
